import React from "react";
import { Autocomplete, Box, Button, Menu, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { Client } from "../../../interfaces/ClientInterface";
import ClientStickerDefaultTemplate from "./Templates/ClientStickerDefaultTemplate";
import { notificationAdd } from "../../../actions/notification";
import { incrementClientQueueNumber } from "../../../api/clientQueueNumber";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";
import { useIncrementQueueOnPrint } from "../../../hooks/useIncrementQueueOnPrint";
import MultipleButtonGroup, { PRINT_FROM } from "../../../components/MultipleButtonGroup";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";
import { RootState } from "../../../store";
import { saveClient } from "../../../actions/client";
import { getClientDetails } from "../../../api/client";
import { ClientStickerTemplate2 } from "./Templates/ClientStickerTemplate2";
import { getSpFullName } from "./StickerUtils";

interface PropType {
  clientId: number;
}

// eslint-disable-next-line import/prefer-default-export
export function ClientStickerPrinter({ clientId }: PropType): React.ReactNode {
  const dispatch = useDispatch();

  const serviceProviders: ServiceProvider[] = useSelector(
    (state: RootState) => state.resources.resourceCentreServiceProviders
  );

  const [client, setClient] = React.useState<Client | null>(null);

  // states
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedSp, setSelectedSp] = React.useState<ServiceProvider | null>(null);

  // custom hooks
  const resourceCentre = useCurrentResourceCentre();
  const { incrementQueueOnPrint, toggleIncrementQueueOnPrint } = useIncrementQueueOnPrint();

  // helpers
  const isQueueEnabled = resourceCentre?.settings?.queueSettings?.enableQueueSystem;

  // use effect
  React.useEffect(() => {
    (async () => {
      if (!clientId) return;

      try {
        const clt = await getClientDetails(clientId);
        setClient(clt);
        if (clt?.lastHandledSpId) {
          setSelectedSp(
            serviceProviders.find((sp) => Number(sp.id) === Number(clt.lastHandledSpId)) || null
          );
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        dispatch(
          notificationAdd({
            id: new Date().getMilliseconds(),
            variant: "error",
            message: err?.message || err?.data?.message || "Error while fetching client detail.",
            autoTimeout: true
          })
        );
      }
    })();
  }, [client?.lastHandledSpId, clientId, dispatch, serviceProviders]);

  // return null if not client id provided
  if (!clientId || !client) return null;

  return (
    <>
      <Button
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
        size="small"
        variant="outlined"
      >
        Print Sticker
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        sx={{ p: 2 }}
      >
        <Box sx={{ width: "300px", p: 2 }}>
          <Autocomplete
            value={selectedSp}
            onChange={(e, sp: ServiceProvider | null) => {
              if (sp && client) {
                // get the lastest queue number, this triggers effect
                setSelectedSp(sp);

                dispatch(
                  saveClient(
                    {
                      id: client.id,
                      lastHandledSpId: sp?.id || null,
                      firstName: client.firstName,
                      lastName: client.lastName
                    } as Client,
                    true
                  )
                );
              }
            }}
            selectOnFocus
            getOptionLabel={(option) => getSpFullName(option)}
            options={serviceProviders.filter((sp) => sp.active)}
            renderInput={(params) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <TextField variant="outlined" {...params} label="Select Service Providers" />
            )}
          />

          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <MultipleButtonGroup
              printFrom={PRINT_FROM.CLIENT}
              incrementQueueOnPrint={incrementQueueOnPrint}
              toggleIncrementQueueOnPrint={toggleIncrementQueueOnPrint}
              options={[
                {
                  label: "Print Default",
                  onClickAction: () => ({}),
                  component: (
                    <ClientStickerDefaultTemplate
                      serviceProviderId={selectedSp?.id ?? undefined}
                      client={client}
                      trigger={<Button variant="contained">Print Default</Button>}
                      onPrintClick={async () => {
                        if (
                          isQueueEnabled &&
                          incrementQueueOnPrint &&
                          selectedSp?.id &&
                          resourceCentre?.id
                        ) {
                          try {
                            await incrementClientQueueNumber({
                              resourceCentreId: resourceCentre.id,
                              serviceProviderId: selectedSp.id,
                              departmentId: selectedSp?.departmentId || null
                            });
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          } catch (err: any) {
                            dispatch(
                              notificationAdd({
                                id: new Date().getTime(),
                                message:
                                  err?.mesage || err?.data?.message || "Error while fetching ",
                                variant: "error",
                                autoTimeout: true
                              })
                            );
                          }
                        }
                      }}
                    />
                  )
                },
                {
                  label: "Print Template 2",
                  onClickAction: () => ({}),
                  component: (
                    <ClientStickerTemplate2
                      serviceProviderId={selectedSp?.id ?? undefined}
                      client={client}
                      trigger={<Button variant="contained">Print Template 2</Button>}
                      onPrintClick={async () => {
                        if (
                          isQueueEnabled &&
                          incrementQueueOnPrint &&
                          selectedSp?.id &&
                          resourceCentre?.id
                        ) {
                          try {
                            await incrementClientQueueNumber({
                              resourceCentreId: resourceCentre.id,
                              serviceProviderId: selectedSp.id,
                              departmentId: selectedSp?.departmentId || null
                            });
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          } catch (err: any) {
                            dispatch(
                              notificationAdd({
                                id: new Date().getTime(),
                                message:
                                  err?.mesage || err?.data?.message || "Error while fetching ",
                                variant: "error",
                                autoTimeout: true
                              })
                            );
                          }
                        }
                      }}
                    />
                  )
                }
              ]}
            />
          </Box>
        </Box>
      </Menu>
    </>
  );
}
